import axios from 'axios';

// Allow REACT_APP_API_URL to be either a full URL or a bare hostname
// (Render's fromService.property: host returns just the hostname).
const _apiRaw = process.env.REACT_APP_API_URL || '';
const API_BASE = _apiRaw && !/^https?:\/\//i.test(_apiRaw) ? `https://${_apiRaw}` : _apiRaw;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear admin session (but don't redirect — chat is public)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('username');
    }
    return Promise.reject(err);
  }
);

// ── Auth ───────────────────────────────────────────────────────────────────────
export const login = (username, password) =>
  api.post('/api/auth/login', { username, password });

// Public — drives which login paths the UI shows (local form vs SSO button).
export const getAuthConfig = () => api.get('/api/auth/config');

// Exchange an Entra ID id_token for a DASH-issued JWT.
export const ssoExchange = (idToken) =>
  api.post('/api/auth/sso/exchange', { id_token: idToken });

// ── Documents ──────────────────────────────────────────────────────────────────
export const getDocuments = () => api.get('/api/documents');

export const uploadDocument = (file, onProgress, products = []) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('products', JSON.stringify(products));
  return api.post('/api/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress) {
        onProgress(Math.round((e.loaded * 100) / e.total));
      }
    },
  });
};

export const getProducts = () => api.get('/api/documents/products');

export const deleteDocument = (id) => api.delete(`/api/documents/${id}`);

export const deleteAllDocuments = () => api.delete('/api/documents/all');

export const reindexDocuments = () => api.post('/api/documents/reindex');

// ── Query ──────────────────────────────────────────────────────────────────────
// Pass topK=null to use the backend's TOP_K (.env) setting.
export const queryDocuments = (question, topK, history = [], product = null) =>
  api.post('/api/query', {
    question,
    ...(topK ? { top_k: topK } : {}),
    ...(history.length ? { history } : {}),
    ...(product ? { product } : {}),
  });

// Streaming query — returns EventSource-compatible fetch
export const streamQuery = async (question, topK, onChunk, onSources, onDone, onQueryId, history = [], product = null) => {
  const token = localStorage.getItem('access_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const body = { question, stream: true };
  if (topK) body.top_k = topK;
  if (history.length) body.history = history;
  if (product) body.product = product;
  const response = await fetch(`${API_BASE}/api/query/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new Error('Stream request failed');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();

    for (let line of lines) {
      line = line.replace(/\r$/, ''); // strip \r from \r\n line endings
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6);
      if (data === '[DONE]') {
        onDone?.();
        return;
      }
      if (data.startsWith('[SOURCES]')) {
        const json = data.slice(9, data.indexOf('[/SOURCES]'));
        onSources?.(JSON.parse(json));
      } else if (data.startsWith('[QUERY_ID]')) {
        const id = parseInt(data.slice(10, data.indexOf('[/QUERY_ID]')), 10);
        if (!Number.isNaN(id)) onQueryId?.(id);
      } else {
        // |NL| markers are replaced with actual newlines (preserved through SSE framing)
        onChunk?.(data.replace(/\|NL\|/g, '\n'));
      }
    }
  }
  onDone?.();
};

export const getHealth = () => api.get('/health');

// ── Analytics (admin) ──────────────────────────────────────────────────────────
export const analyticsOverview     = (range = '30d')     => api.get('/api/analytics/overview',      { params: { range } });
export const analyticsTopQuestions = (range = '30d', limit = 20) => api.get('/api/analytics/top-questions', { params: { range, limit } });
export const analyticsGaps         = (range = '30d', limit = 20) => api.get('/api/analytics/gaps',          { params: { range, limit } });
export const analyticsProducts     = (range = '30d')     => api.get('/api/analytics/products', { params: { range } });
export const analyticsTiming       = (range = '30d')     => api.get('/api/analytics/timing',   { params: { range } });
export const analyticsFeedback     = (range = '30d')     => api.get('/api/analytics/feedback', { params: { range } });
export const analyticsUsage        = (range = '30d')     => api.get('/api/analytics/usage',    { params: { range } });
export const analyticsChips        = (range = '30d')     => api.get('/api/analytics/chips',    { params: { range } });
export const analyticsExportCsvUrl = (range = '30d')     => `${API_BASE}/api/analytics/export.csv?range=${encodeURIComponent(range)}`;

// Excel export goes through axios so we can attach the bearer token,
// then trigger a browser download from the returned blob.
export const downloadAnalyticsXlsx = async (range = '30d') => {
  const res = await api.get('/api/analytics/export.xlsx', {
    params: { range }, responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const a = document.createElement('a');
  a.href = url; a.download = `dash_analytics_${range}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};

// ── Feedback (public) ──────────────────────────────────────────────────────────
// Optional fields collected from the chat feedback popup (first_name, last_name, email).
export const postFeedback = (queryId, rating, comment = null, contact = null) =>
  api.post('/api/feedback', {
    query_id: queryId,
    rating,
    comment,
    ...(contact?.first_name ? { first_name: contact.first_name } : {}),
    ...(contact?.last_name  ? { last_name:  contact.last_name  } : {}),
    ...(contact?.email      ? { email:      contact.email      } : {}),
  });

// Admin/viewer — list every feedback submission for the dashboard tab.
export const getFeedbackList = (limit = 200) =>
  api.get('/api/analytics/feedback-list', { params: { limit } });

// ── Curated Answers (admin) ────────────────────────────────────────────────────
export const listCurated   = ()                    => api.get('/api/curated');
export const getCurated    = (id)                  => api.get(`/api/curated/${id}`);
export const createCurated = (payload)             => api.post('/api/curated', payload);
export const updateCurated = (id, payload)         => api.put(`/api/curated/${id}`, payload);
export const deleteCurated = (id)                  => api.delete(`/api/curated/${id}`);
export const testCurated   = (question, product)   =>
  api.post('/api/curated/test', { question, ...(product ? { product } : {}) });
export const formatCurated = (rawText, question)    =>
  api.post('/api/curated/format', { raw_text: rawText, ...(question ? { question } : {}) });

// ── Chip click tracking (public) ───────────────────────────────────────────────
export const postChipClick = (label, group) =>
  api.post('/api/chip-click', { label, group });

export default api;
